import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { apiClient } from "../../App";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import {
  ArrowLeft,
  Search,
  FileText,
  Building2,
  Palette,
  Layers,
  MoreHorizontal,
  Copy,
  Trash2,
  ExternalLink,
  Filter
} from "lucide-react";

const STATUS_OPTIONS = [
  { value: "all", label: "All Statuses" },
  { value: "draft", label: "Draft" },
  { value: "internal_review", label: "Internal Review" },
  { value: "sent", label: "Sent" },
  { value: "won", label: "Won" },
  { value: "lost", label: "Lost" }
];

const TYPE_OPTIONS = [
  { value: "all", label: "All Types" },
  { value: "vexos", label: "VEXOS" },
  { value: "visualization", label: "Visualization" },
  { value: "combined", label: "Combined" }
];

export default function SavedQuotes() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState(searchParams.get("status") || "all");
  const [typeFilter, setTypeFilter] = useState(searchParams.get("type") || "all");

  useEffect(() => {
    fetchQuotes();
  }, [statusFilter, typeFilter]);

  async function fetchQuotes() {
    try {
      let url = "/calculators/quotes?limit=100";
      if (statusFilter !== "all") url += `&status=${statusFilter}`;
      if (typeFilter !== "all") url += `&quote_type=${typeFilter}`;
      
      const res = await apiClient.get(url);
      setQuotes(res.data);
    } catch (err) {
      toast.error("Failed to load quotes");
    } finally {
      setLoading(false);
    }
  }

  async function handleDuplicate(quoteId) {
    try {
      const res = await apiClient.post(`/calculators/quotes/${quoteId}/duplicate`);
      toast.success("Quote duplicated");
      navigate(`/sales/calculators/${res.data.quote_type}/${res.data.id}`);
    } catch (err) {
      toast.error("Failed to duplicate quote");
    }
  }

  async function handleDelete(quoteId) {
    if (!confirm("Are you sure you want to delete this quote?")) return;
    try {
      await apiClient.delete(`/calculators/quotes/${quoteId}`);
      toast.success("Quote deleted");
      fetchQuotes();
    } catch (err) {
      toast.error("Failed to delete quote");
    }
  }

  async function handleStatusChange(quoteId, newStatus) {
    try {
      await apiClient.put(`/calculators/quotes/${quoteId}`, { status: newStatus });
      toast.success("Status updated");
      fetchQuotes();
    } catch (err) {
      toast.error("Failed to update status");
    }
  }

  const filteredQuotes = quotes.filter(q => {
    if (!search) return true;
    const term = search.toLowerCase();
    return (
      q.project_name?.toLowerCase().includes(term) ||
      q.client_name?.toLowerCase().includes(term) ||
      q.salesperson?.toLowerCase().includes(term)
    );
  });

  const statusColors = {
    draft: "bg-gray-500/10 text-gray-500 border-gray-500/20",
    internal_review: "bg-amber-500/10 text-amber-500 border-amber-500/20",
    sent: "bg-blue-500/10 text-blue-500 border-blue-500/20",
    won: "bg-emerald-500/10 text-emerald-500 border-emerald-500/20",
    lost: "bg-red-500/10 text-red-500 border-red-500/20"
  };

  const typeIcons = {
    vexos: Building2,
    visualization: Palette,
    combined: Layers
  };

  const typeColors = {
    vexos: "text-blue-500",
    visualization: "text-purple-500",
    combined: "text-emerald-500"
  };

  // Stats
  const stats = {
    total: quotes.length,
    totalValue: quotes.reduce((sum, q) => sum + (q.grand_total || 0), 0),
    won: quotes.filter(q => q.status === "won").length,
    wonValue: quotes.filter(q => q.status === "won").reduce((sum, q) => sum + (q.grand_total || 0), 0)
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/sales/calculators")}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-light">Saved Quotes</h1>
            <p className="text-muted-foreground text-sm">
              View and manage all your quotes
            </p>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <p className="text-2xl font-light">{stats.total}</p>
            <p className="text-sm text-muted-foreground">Total Quotes</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <p className="text-2xl font-light">${stats.totalValue.toLocaleString()}</p>
            <p className="text-sm text-muted-foreground">Total Pipeline</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <p className="text-2xl font-light text-emerald-500">{stats.won}</p>
            <p className="text-sm text-muted-foreground">Won</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <p className="text-2xl font-light text-emerald-500">${stats.wonValue.toLocaleString()}</p>
            <p className="text-sm text-muted-foreground">Won Value</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="py-4">
          <div className="flex items-center gap-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search quotes..."
                className="pl-9"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TYPE_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Quotes Table */}
      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Quote</TableHead>
              <TableHead>Client</TableHead>
              <TableHead>Salesperson</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Value</TableHead>
              <TableHead className="text-right">Updated</TableHead>
              <TableHead className="w-12"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredQuotes.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-12 text-muted-foreground">
                  <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No quotes found</p>
                </TableCell>
              </TableRow>
            ) : (
              filteredQuotes.map((quote) => {
                const Icon = typeIcons[quote.quote_type] || FileText;
                return (
                  <TableRow key={quote.id} className="cursor-pointer hover:bg-muted/50">
                    <TableCell>
                      <Link 
                        to={`/sales/calculators/${quote.quote_type}/${quote.id}`}
                        className="font-medium hover:underline"
                      >
                        {quote.project_name}
                      </Link>
                    </TableCell>
                    <TableCell>{quote.client_name}</TableCell>
                    <TableCell>{quote.salesperson}</TableCell>
                    <TableCell>
                      <div className={`flex items-center gap-2 ${typeColors[quote.quote_type]}`}>
                        <Icon className="w-4 h-4" />
                        <span className="capitalize">{quote.quote_type}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Select 
                        value={quote.status} 
                        onValueChange={(v) => handleStatusChange(quote.id, v)}
                      >
                        <SelectTrigger className="h-7 w-32 border-0 bg-transparent p-0">
                          <Badge variant="outline" className={statusColors[quote.status]}>
                            {quote.status.replace("_", " ")}
                          </Badge>
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="draft">Draft</SelectItem>
                          <SelectItem value="internal_review">Internal Review</SelectItem>
                          <SelectItem value="sent">Sent</SelectItem>
                          <SelectItem value="won">Won</SelectItem>
                          <SelectItem value="lost">Lost</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      ${quote.grand_total?.toLocaleString() || 0}
                    </TableCell>
                    <TableCell className="text-right text-muted-foreground text-sm">
                      {new Date(quote.updated_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => navigate(`/sales/calculators/${quote.quote_type}/${quote.id}`)}>
                            <ExternalLink className="w-4 h-4 mr-2" />
                            Open
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleDuplicate(quote.id)}>
                            <Copy className="w-4 h-4 mr-2" />
                            Duplicate
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => handleDelete(quote.id)}
                            className="text-red-500"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
